// Editing a waitlist request from the portal.
//
// ## The warning is the point of this dialog
//
// Editing is a close and a create, and the queue position only survives when
// the SERVICE is unchanged: `createdAt` records WHEN THEY ASKED, and asking for
// something else is a different request. Changing the hours somebody can attend
// does not cost them anything, which is deliberate — narrowing your
// availability is the thing you want a patient to do.
//
// So a patient changing the service is told, before they save, that they go to
// the back of the queue. Letting them discover it afterwards from a list that
// silently reordered is the version of this screen that generates phone calls.
//
// After saving, what is reported comes from the server's `keptQueuePosition`
// rather than from comparing the fields here. The rule belongs to the engine,
// and a second copy of it in the browser is one that can disagree.

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Select,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import {useEffect, useMemo, useState} from "react";
import {FiPlus, FiTrash2} from "react-icons/fi";
import {DAY_NAMES} from "./describeBands";
import {optionsIncludingCurrent} from "./selectOptions";
import {cliniciansForService} from "./locationScope";
import {durationChoices, formatMinutes} from "./serviceDurations";

/** Minutes from midnight <-> the value an <input type="time"> carries. */
function minuteToTime(minute) {
  const total = Math.max(0, Math.min(1439, Number(minute) || 0));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
    total % 60
  ).padStart(2, "0")}`;
}

function timeToMinute(value) {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

/** The one comparison the warning and the duration reset both depend on. */
function serviceChangedRef(entry, serviceId) {
  if (!entry) return false;
  return String(serviceId) !== String(entry.serviceId);
}

export default function WaitlistRequestDialog({
  isOpen,
  onClose,
  entry,
  services = [],
  clinicians = [],
  // The names the LIST already resolved. Passed in rather than looked up again,
  // so a record naming something the portal did not load can still be named on
  // screen instead of shown as a raw id.
  serviceName = "",
  clinicianName = "",
  onSave,
  isSaving,
}) {
  const [serviceId, setServiceId] = useState("");
  const [clinicianId, setClinicianId] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(null);
  const [bands, setBands] = useState([]);
  const [error, setError] = useState("");
  // WHICH entry the fields currently hold.
  //
  // Without it the duration effect below ran on the first render of an open
  // dialog, when `serviceId` is still "" and the entry's service has not been
  // copied in yet. It read that as "the patient changed the service", looked up
  // a service that did not exist, and stamped `parseDurationMinutes`'s fallback
  // of 45 over whatever the patient had actually agreed to. Verified on screen:
  // a stored 30 showed as 45, and saving would have written 45.
  const [syncedEntryId, setSyncedEntryId] = useState(null);

  useEffect(() => {
    if (!isOpen || !entry) return;
    setServiceId(entry.serviceId ? String(entry.serviceId) : "");
    setDurationMinutes(entry.requestedDurationMinutes ?? null);
    // `null` means ANY clinician and is the common case. The empty string is
    // this form's spelling of it, converted back at the border.
    setClinicianId(entry.clinicianId || "");
    setBands(
      (entry.bands || []).map((band) => ({
        dayOfWeek: band.dayOfWeek,
        startMinute: band.startMinute,
        endMinute: band.endMinute,
      }))
    );
    setError("");
    setSyncedEntryId(entry.entryId);
  }, [isOpen, entry]);

  const serviceChanged = serviceChangedRef(entry, serviceId);

  const selectedService = useMemo(
    () =>
      services.find(
        (service) => String(service.serviceId) === String(serviceId)
      ) || null,
    [services, serviceId]
  );

  // The service's own choices, plus whatever this entry already holds. Same
  // rule as the two lists below: a duration the clinic has since stopped
  // offering is still the one the patient agreed to, and a <select> with no
  // option for its value renders the first one — so dropping it would show a
  // length nobody chose and write it on the next save.
  const durations = useMemo(() => {
    const {options} = durationChoices(selectedService);
    const all = new Set(options);
    if (Number.isInteger(durationMinutes) && durationMinutes > 0) {
      all.add(durationMinutes);
    }
    return [...all].sort((a, b) => a - b);
  }, [selectedService, durationMinutes]);

  // A new service brings its own default; keeping the old number would offer a
  // length the new service does not sell.
  //
  // Guarded twice, and both guards earned their place:
  //   - `syncedEntryId` means the fields already hold THIS entry, so a service
  //     that differs is one the patient chose rather than one not copied in yet.
  //   - `selectedService` means the new service is one we can actually read a
  //     default from. With no service there is nothing to know, and writing a
  //     fallback is inventing a number the patient never agreed to.
  useEffect(() => {
    if (!entry || syncedEntryId !== entry.entryId) return;
    if (!serviceChangedRef(entry, serviceId)) return;
    if (!selectedService) return;
    const {defaultMinutes} = durationChoices(selectedService);
    if (defaultMinutes > 0) setDurationMinutes(defaultMinutes);
  }, [serviceId, selectedService, entry, syncedEntryId]);

  // A <select> whose value matches no option renders the FIRST one, while state
  // still holds the real value. The screen then says "Botox" for a request that
  // is Psychological Services, and the patient edits something they were never
  // shown. It happens whenever the entry names something the portal did not
  // load: a service retired since, or a clinician at a site this patient's
  // `dashState.doctors` does not cover.
  //
  // So anything the entry holds gets an option of its own, marked as no longer
  // offered. Showing the truth and saying it is unavailable beats showing a
  // convincing lie.
  const serviceOptions = useMemo(
    () =>
      optionsIncludingCurrent(
        services.map((service) => ({
          value: String(service.serviceId),
          label: service.serviceName || String(service.serviceId),
        })),
        serviceId,
        `${serviceName || "This service"} (no longer offered)`
      ),
    [services, serviceId, serviceName]
  );

  // Narrowed by the service CURRENTLY selected, not by the one the entry was
  // saved with. The parent has already scoped this list to the entry's
  // location; the service is the second half of the same question, and it
  // changes while the dialog is open. The clinic form re-narrows the same way.
  const clinicianOptions = useMemo(
    () =>
      optionsIncludingCurrent(
        cliniciansForService(clinicians, serviceId).map((clinician) => ({
          value: clinician.did,
          label:
            `${clinician.firstName || ""} ${clinician.lastName || ""}`.trim() ||
            clinician.name ||
            clinician.did,
        })),
        clinicianId,
        `${clinicianName || clinicianId} (not available here)`
      ),
    [clinicians, serviceId, clinicianId, clinicianName]
  );

  function addBand() {
    setBands((current) => [
      ...current,
      {dayOfWeek: 1, startMinute: 540, endMinute: 1020},
    ]);
  }

  function updateBand(index, patch) {
    setBands((current) =>
      current.map((band, at) => (at === index ? {...band, ...patch} : band))
    );
  }

  function removeBand(index) {
    setBands((current) => current.filter((_, at) => at !== index));
  }

  async function handleSave() {
    if (!serviceId) {
      setError("Choose what you are waiting for.");
      return;
    }
    for (const band of bands) {
      if (band.endMinute <= band.startMinute) {
        setError("Each time range has to end after it starts.");
        return;
      }
    }
    setError("");

    await onSave({
      serviceId,
      // Never "": an empty string is a value in the id domain that happens to
      // be no id, and the engine reads `null` as ANY.
      clinicianId: clinicianId || null,
      requestedDurationMinutes:
        Number(durationMinutes) || entry.requestedDurationMinutes,
      validUntil: entry.validUntil,
      minimumNoticeMinutes: entry.minimumNoticeMinutes ?? 0,
      kind: entry.kind || "standalone",
      ...(entry.kind === "anchored"
        ? {anchorAppointmentId: entry.anchorAppointmentId}
        : {}),
      bands,
    });
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">Edit waitlist request</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">What you are waiting for</FormLabel>
              <Select
                value={serviceId}
                onChange={(event) => setServiceId(event.target.value)}
                size="sm"
              >
                {serviceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {serviceChanged && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <AlertDescription>
                  Changing what you are waiting for files a new request, so you
                  lose your place in the queue. Changing only your availability
                  keeps it.
                </AlertDescription>
              </Alert>
            )}

            <FormControl>
              <FormLabel fontSize="sm">How long you need</FormLabel>
              <Select
                value={durationMinutes ?? ""}
                onChange={(event) =>
                  setDurationMinutes(Number(event.target.value))
                }
                size="sm"
              >
                {durations.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {formatMinutes(minutes)}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Who you would like to see</FormLabel>
              <Select
                value={clinicianId}
                onChange={(event) => setClinicianId(event.target.value)}
                size="sm"
              >
                <option value="">Anyone available</option>
                {clinicianOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel fontSize="sm" mb={0}>
                  When you can come in
                </FormLabel>
                <Button
                  size="xs"
                  leftIcon={<FiPlus />}
                  variant="ghost"
                  onClick={addBand}
                >
                  Add a time
                </Button>
              </Flex>

              {bands.length === 0 ? (
                <Text fontSize="13px" color="#6b7280">
                  Any day, any time. Add a range if only some times work for
                  you — the fewer you leave, the fewer openings can reach you.
                </Text>
              ) : (
                <VStack spacing={2} align="stretch">
                  {bands.map((band, index) => (
                    <Flex key={index} gap={2} align="center">
                      <Select
                        size="sm"
                        value={band.dayOfWeek}
                        onChange={(event) =>
                          updateBand(index, {
                            dayOfWeek: Number(event.target.value),
                          })
                        }
                      >
                        {DAY_NAMES.map((day, at) => (
                          <option key={day} value={at}>
                            {day}
                          </option>
                        ))}
                      </Select>
                      <Input
                        size="sm"
                        type="time"
                        value={minuteToTime(band.startMinute)}
                        onChange={(event) => {
                          const minute = timeToMinute(event.target.value);
                          if (minute !== null)
                            updateBand(index, {startMinute: minute});
                        }}
                      />
                      <Input
                        size="sm"
                        type="time"
                        value={minuteToTime(band.endMinute)}
                        onChange={(event) => {
                          const minute = timeToMinute(event.target.value);
                          if (minute !== null)
                            updateBand(index, {endMinute: minute});
                        }}
                      />
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label="Remove this time"
                        icon={<FiTrash2 />}
                        onClick={() => removeBand(index)}
                      />
                    </Flex>
                  ))}
                </VStack>
              )}
            </Box>

            {error && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
